import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Alert,
  Skeleton,
  Link,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { getFullNutritionLabelLink } from "../services/source-data.service";
import { fillParent, scrollingBox } from "utils/base-styles";
import { parse } from "tldts"; // Import tldts parser

import NewsguardScore from "components/base/NewsguardScore";
import newsGuardImg from "assets/images/newsguard.png";
import newsGuardImgDark from "assets/images/newsguard2.png";
import three_star from "assets/images/three_star.svg";
import four_star from "assets/images/four_star.svg";
import one_star from "assets/images/one_star.svg";
import zero_star from "assets/images/zero_star.svg";
import two_star from "assets/images/two_star.svg";
import NewsguardCriteria from "./NewsGuardCriteria";
import SourceInfoBox from "./SourceInfoBox";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import { mediaBiasContent } from "data/mediaBiasContent";
import { logEvent } from "services/amplitude.service";
import AllSidesLogo from "components/base/AllSidesLogo";
import { mediaBiasUrl } from "data/mediabias-url";
import { useSourceData } from "../hooks/use-source-data.hooks";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export default function SourceInfo() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navbarBackground = theme.palette.secondary.main;

  const {
    sourceNotFound,
    sourceData,
    sourceUrl,
    isLoading,
    handleCloseSourceSidebar,
  } = useSourceData();
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [labelLink, setLabelLink] = useState("");
  const [showMore, setShowMore] = useState(false);
  const maxChars = 400;

  const getNutritionLink = async () => {
    if (labelLink) {
      window.open(labelLink, "_blank");
      return;
    }

    setNutritionLoading(true);

    const newTab = window.open("about:blank", "_blank");

    try {
      const url = await getFullNutritionLabelLink(
        sourceData.hostname,
        sourceData.score
      );

      setLabelLink(url);

      if (newTab) {
        newTab.location.href = url;
      }
    } catch (error) {
      console.error("Failed to Fetch Nutrition Data", error);
      if (newTab) {
        newTab.close();
      }
    } finally {
      setNutritionLoading(false);
    }
  };

  const charity_rating = () => {
    if (
      sourceData.charityNavigatorScore > 90 ||
      sourceData.charityNavigatorRating === 4
    ) {
      return four_star;
    } else if (
      sourceData.charityNavigatorScore >= 75 ||
      sourceData.charityNavigatorRating === 3
    ) {
      return three_star;
    } else if (
      sourceData.charityNavigatorScore >= 60 ||
      sourceData.charityNavigatorRating === 2
    ) {
      return two_star;
    } else if (
      sourceData.charityNavigatorScore >= 50 ||
      sourceData.charityNavigatorRating === 1
    ) {
      return one_star;
    }
    return zero_star;
  };

  const charity_text = () => {
    if (sourceData.charityNavigatorScore >= 90) {
      return "four-star";
    } else if (sourceData.charityNavigatorScore >= 75) {
      return "three-star";
    } else if (sourceData.charityNavigatorScore >= 60) {
      return "two-star";
    } else if (sourceData.charityNavigatorScore >= 50) {
      return "one-star";
    }
    return "zero-star";
  };

  useEffect(() => {
    window.addEventListener(
      "tour:app:sourceInfo:close",
      handleCloseSourceSidebar
    );
    return () => {
      window.removeEventListener(
        "tour:app:sourceInfo:close",
        handleCloseSourceSidebar
      );
    };
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateRows: `minmax(auto, auto) 1fr`,
        minHeight: "100%",
        minWidth: "35vw",
      }}
    >
      <Box
        sx={{
          backgroundColor: navbarBackground,
          p: 3,
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ color: "grey.900", fontSize: "2rem" }}>
            {isLoading ? (
              <Skeleton variant="text" width={100} />
            ) : sourceData == null ? (
              sourceUrl
            ) : (
              sourceData.entityName
            )}
          </Typography>
          <AccessibleIconButton
            title="Close"
            onClick={handleCloseSourceSidebar}
          >
            <CloseIcon style={{ height: 18, width: 18 }} />
          </AccessibleIconButton>
        </Box>
        {sourceNotFound && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography>
              Could not find information about this source.
            </Typography>
          </Alert>
        )}
        <Box sx={{ mt: 1 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={100} />
          ) : (
            <Fragment>
              {sourceData?.wikipediaSnippet ? (
                <Typography variant="body1" component="div">
                  {(() => {
                    const fullText = sourceData.wikipediaSnippet.replace(
                      /\n+$/,
                      ""
                    );
                    const isLong = fullText.length > maxChars;
                    return showMore || !isLong
                      ? fullText
                      : fullText.slice(0, maxChars) + "...";
                  })()}

                  {sourceData.wikipediaSnippet.replace(/\n+$/, "").length >
                    maxChars && (
                    <Typography
                      variant="body2"
                      color="primary"
                      onClick={() => setShowMore((prev) => !prev)}
                      sx={{ cursor: "pointer", mt: 1 }}
                    >
                      {showMore ? "Show Less" : "Show More"}
                    </Typography>
                  )}

                  {sourceData?.wikipediaUrl && (
                    <Typography sx={{ mt: 1, color: "grey.700" }}>
                      - Source:{" "}
                      <Link
                        href={sourceData.wikipediaUrl}
                        target="_blank"
                        underline="hover"
                        sx={{ color: "inherit", textDecoration: "none" }}
                      >
                        Wikipedia
                      </Link>
                    </Typography>
                  )}
                </Typography>
              ) : (
                sourceData?.snippet && (
                  <Fragment>
                    <Typography variant="body1" component="div">
                      {(() => {
                        const fullText = sourceData.snippet.replace(/\n+$/, "");
                        const isLong = fullText.length > maxChars;
                        return showMore || !isLong
                          ? fullText
                          : fullText.slice(0, maxChars) + "...";
                      })()}
                    </Typography>

                    {sourceData.snippet.replace(/\n+$/, "").length >
                      maxChars && (
                      <Typography
                        variant="body2"
                        color="primary"
                        onClick={() => setShowMore((prev) => !prev)}
                        sx={{ cursor: "pointer", mt: 1 }}
                      >
                        {showMore ? "Show Less" : "Show More"}
                      </Typography>
                    )}

                    {sourceData.url && (
                      <Typography sx={{ mt: 1, color: "grey.600" }}>
                        - Source :{" "}
                        <Link
                          href={sourceData.url}
                          target="_blank"
                          underline="hover"
                          sx={{ color: "inherit", textDecoration: "none" }}
                        >
                          {sourceData.url
                            .toLowerCase()
                            .includes(
                              sourceData.entityName
                                .toLowerCase()
                                .replace(/\s+/g, "")
                            )
                            ? sourceData.entityName
                            : parse(sourceData.url)?.domain}
                        </Link>
                      </Typography>
                    )}
                  </Fragment>
                )
              )}
            </Fragment>
          )}
        </Box>
      </Box>

      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 3,
            gap: 3,
            mb: isMobile && 6,
            ...fillParent,
            ...scrollingBox,
          }}
        >
          {!isLoading ? (
            <Fragment>
              {sourceData?.score ? (
                <SourceInfoBox
                  id="tour-newsguard-source-info"
                  buttonId="tour-newsguard-source-button"
                  title="NewsGuard Score"
                  header={
                    <NewsguardScore score={sourceData.score} size="large" />
                  }
                  buttonText="Full Nutrition Label"
                  buttonLink={labelLink}
                  loading={nutritionLoading}
                  onClick={() => {
                    getNutritionLink();
                    logEvent("click_newsguard_nutritionlabel", {
                      click: true,
                      link: getNutritionLink,
                    });
                  }}
                  logo={
                    <img
                      style={{
                        width: "10rem",
                        cursor: "pointer",
                      }}
                      src={
                        theme.palette.mode === "light"
                          ? newsGuardImg
                          : newsGuardImgDark
                      }
                      onClick={() => {
                        window.open(
                          "https://www.newsguardtech.com/about-newsguard/",
                          "blank"
                        );
                        logEvent("click_newsguard_logo", {
                          click: true,
                          link: "https://www.newsguardtech.com/about-newsguard/",
                        });
                      }}
                      alt="news guard"
                    />
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: "50%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Typography color="primary.dark" variant="h6">
                        Credibility
                      </Typography>
                      <NewsguardCriteria
                        value={sourceData.falseContent}
                        criteria="Does not repeatedly publish false or egregiously misleading content"
                      />
                      <NewsguardCriteria
                        value={sourceData.basicStandards}
                        criteria="Gathers and presents information responsibly"
                      />
                      <NewsguardCriteria
                        value={sourceData.accountabilityPractices}
                        criteria="Has effective practices for correcting errors"
                      />
                      <NewsguardCriteria
                        value={sourceData.newsOpinion}
                        criteria="Handles the difference between news and opinion
                        responsibly"
                      />
                      <NewsguardCriteria
                        value={sourceData.deceptiveHeadlines}
                        criteria="Avoids deceptive headlines"
                      />
                    </Box>
                    <Box
                      sx={{
                        width: "50%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Typography color="primary.dark" variant="h6">
                        Transparency
                      </Typography>
                      <NewsguardCriteria
                        value={sourceData.ownership}
                        criteria="Website discloses ownership and financing"
                      />
                      <NewsguardCriteria
                        value={sourceData.labelsAdvertising}
                        criteria="Clearly labels advertising"
                      />
                      <NewsguardCriteria
                        value={sourceData.management}
                        criteria="Reveals who's in charge, including any possible
                        conflicts of interest"
                      />
                      <NewsguardCriteria
                        value={sourceData.contentCreators}
                        criteria="This site provides the names of content creators,
                        along with either contact or biographical information"
                      />
                    </Box>
                  </Box>
                </SourceInfoBox>
              ) : (
                <Alert variant="outlined" icon={false} severity="warning">
                  Credibility score is unavailable
                </Alert>
              )}
              {sourceData?.mediaBiasRating ? (
                <SourceInfoBox
                  id="tour-all-sides-source-info"
                  buttonId="tour-all-sides-source-button"
                  title={
                    <Fragment>
                      AllSides Media Bias Rating
                      <Typography
                        component="sup"
                        variant="h6"
                        color="primary.dark"
                        fontSize="0.6em"
                      >
                        TM
                      </Typography>
                    </Fragment>
                  }
                  header={
                    <Button
                      LinkComponent={Link}
                      // variant="outlined"
                      // size="small"
                      href={mediaBiasUrl(sourceData.mediaBiasRating)}
                      target="_blank"
                      onClick={() => {
                        logEvent("click_allsides_url", {
                          click: true,
                          link: mediaBiasUrl(sourceData.mediaBiasRating),
                        });
                      }}
                    >
                      <Typography
                        component="span"
                        fontWeight="bold"
                        fontSize="1.8rem"
                      >
                        {sourceData.mediaBiasRating}
                      </Typography>
                    </Button>
                  }
                  buttonText="Full Info"
                  buttonLink={sourceData.allsidesUrl}
                  onClick={() =>
                    logEvent("click_allsides_mediabiasrating", {
                      click: true,
                      link: sourceData.allsidesUrl,
                    })
                  }
                  logo={
                    <Link
                      href="https://www.allsides.com/about"
                      target="_blank"
                      onClick={() => {
                        logEvent("click_allsides_logo", {
                          click: true,
                          link: "https://www.allsides.com/about",
                        });
                      }}
                    >
                      <AllSidesLogo fontSize="2rem" />
                    </Link>
                  }
                >
                  {sourceData.mediaBiasRating && (
                    <AllSidesRating
                      activeCategory={sourceData.mediaBiasRating}
                      size="large"
                    />
                  )}
                  {sourceData.mediaBiasRating && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {mediaBiasContent(sourceData.mediaBiasRating)}
                    </Typography>
                  )}
                </SourceInfoBox>
              ) : (
                <Alert variant="outlined" icon={false} severity="warning">
                  Media bias rating is unavailable
                </Alert>
              )}

              {sourceData?.charityNavigatorScore ||
              sourceData?.charityNavigatorRating ? (
                <SourceInfoBox
                  id="tour-charity-source-info"
                  buttonId="tour-charity-source-button"
                  title="Charity Navigator Rating"
                  header={
                    sourceData.charityNavigatorScore ? (
                      <Typography fontSize="1.8rem">
                        {sourceData.charityNavigatorScore}%
                      </Typography>
                    ) : null
                  }
                  buttonText="Full Review"
                  onClick={() =>
                    logEvent("click_charity_review", {
                      click: true,
                      link: sourceData.charityNavigatorEIN,
                    })
                  }
                  buttonLink={sourceData.charityNavigatorEIN}
                  logo={
                    <img
                      src="/images/CharityNav_Logo.png"
                      style={{
                        width: "10rem",
                        cursor: "pointer",
                      }}
                      className="charity-logo"
                      alt="logo"
                    />
                  }
                >
                  {sourceData.charityNavigatorRating ? (
                    <img
                      style={{
                        width: "10rem",
                        height: "2rem",
                        marginLeft: -2,
                      }}
                      src={charity_rating()}
                      alt="Charity rating"
                      onClick={() => {
                        window.open(
                          "https://www.charitynavigator.org/about-us/",
                          "_blank"
                        );
                        logEvent("click_charity_logo", {
                          click: true,
                          link: "https://www.charitynavigator.org/about-us/",
                        });
                      }}
                    />
                  ) : null}

                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {sourceData.charityNavigatorRating &&
                      `The Charity Navigator Rating is ${
                        sourceData.charityNavigatorScore
                      }%, earning it a ${charity_text()} rating.`}
                  </Typography>
                </SourceInfoBox>
              ) : null}
            </Fragment>
          ) : (
            <Box
              sx={{
                display: "flex",
                minHeight: "100%",
                gap: 2,
                flexDirection: "column",
              }}
            >
              <Skeleton
                id="tour-newsguard-source-info"
                variant="rectangular"
                height={150}
              />
              <Skeleton
                id="tour-all-sides-source-info"
                variant="rectangular"
                height={150}
              />
              <Skeleton
                id="tour-charity-source-info"
                variant="rectangular"
                height={150}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
